/**
 * ArrowScroll - горизонтальный скролл с кнопками-стрелками
 * Как на главной странице в секции видео
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Box, IconButton, SxProps, Theme } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

interface ArrowScrollProps {
  children: React.ReactNode;
  /** Styles for outer wrapper */
  sx?: SxProps<Theme>;
  /** Styles for inner scroll container */
  contentSx?: SxProps<Theme>;
  /** CSS class for scroll items (to calculate scroll distance) */
  itemClassName?: string;
  /** Fixed scroll distance in pixels (if no itemClassName) */
  scrollDistance?: number;
}

export const ArrowScroll: React.FC<ArrowScrollProps> = ({
  children,
  sx,
  contentSx,
  itemClassName = 'scroll-item',
  scrollDistance = 200,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    checkArrows();
    const el = scrollRef.current;
    if (!el) return;

    let frameId: number | null = null;
    const throttledCheck = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(() => {
          checkArrows();
          frameId = null;
        });
      }
    };

    el.addEventListener('scroll', throttledCheck);
    window.addEventListener('resize', throttledCheck);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      el.removeEventListener('scroll', throttledCheck);
      window.removeEventListener('resize', throttledCheck);
    };
  }, [checkArrows, children]);

  const getScrollAmount = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return scrollDistance;

    // Scroll by half of the visible width
    return el.clientWidth / 2;
  }, [scrollDistance]);

  const scrollPrev = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      left: el.scrollLeft - getScrollAmount(),
      behavior: 'smooth',
    });
  }, [getScrollAmount]);

  const scrollNext = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      left: el.scrollLeft + getScrollAmount(),
      behavior: 'smooth',
    });
  }, [getScrollAmount]);

  const arrowButtonSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 10,
    background: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    width: { xs: 28, sm: 36 },
    height: { xs: 28, sm: 36 },
    '&:hover': {
      background: 'rgba(185, 28, 28, 0.9)',
    },
  };

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      {/* Left Arrow */}
      {showLeftArrow && (
        <IconButton onClick={scrollPrev} sx={{ ...arrowButtonSx, left: { xs: 4, sm: 8 } }}>
          <ChevronLeft sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        </IconButton>
      )}

      {/* Right Arrow */}
      {showRightArrow && (
        <IconButton onClick={scrollNext} sx={{ ...arrowButtonSx, right: { xs: 4, sm: 8 } }}>
          <ChevronRight sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        </IconButton>
      )}

      {/* Scroll Container */}
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          px: { xs: 1, sm: 2 },
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
